import { ReminderCard } from 'components/reminder-card';
import { ResultsAnswers } from 'components/results-answers';
import { ResultsFeedbackBox } from 'components/results-feedback';
import { ResultsInfoBox } from 'components/results-info-box';
import { ResultsPdf } from 'components/results-pdf';
import { SectionTips } from 'components/section-tips';
import { TopTips } from 'components/top-tips';
import { H3 } from 'components/typography/headings';
import { P } from 'components/typography/text';
import dayjs from 'dayjs';
import { useLazyQuery } from '@apollo/client/react';
import { updateSessionResult } from 'graphql/queries';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/exporting';
import 'highcharts/modules/variable-pie';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import IconHelp from '../../../assets/images/icon-help.svg';
import IconWealth from '../../../assets/images/icon-wealth.svg';
import IconWellbeing from '../../../assets/images/icon-wellbeing.svg';
import IconWorklife from '../../../assets/images/icon-worklife.svg';
import IdeaIcon from '../../../assets/images/idea-icon.svg';
import PassionIcon from '../../../assets/images/passion-icon.svg';
import { useAppState, useOnClickUpdateEvent, useSetLinks } from '../../../services/hooks';
import {
    HeaderStickyBar,
    HeaderStickyBarContent,
    HeaderStickyBarDownloadPdf,
    HeaderStickyBarTitle,
    PdfButtonWrapper,
    ResultsContainer,
    ResultsImprove,
    ResultsImproveText,
    ResultsImproveTitle,
    ResultsSection,
    ResultsSectionImg,
    ResultsSectionScore,
    ResultsSectionTitle,
    ResultsStyle,
    ResultsSubtitle,
    ResultsTitle,
    ResultsTop,
    RuiContainer,
    ScoreColor,
    Strong,
    Tooltip,
    TooltipHelpBox,
    TooltipIcon,
    TooltipText,
} from './results.styles';
import { splitStringByDelimiters } from './splitStringByDelimiters';

export const Results = () => {
    const [isSticky, setIsSticky] = useState(false);
    const headerRef = useRef<HTMLDivElement | null>(null);

    const chartComponentWellbeing = useRef<HighchartsReact.RefObject>(null);
    const chartComponentWorklife = useRef<HighchartsReact.RefObject>(null);
    const chartComponentWealth = useRef<HighchartsReact.RefObject>(null);

    const { downloadPDFLink1, downloadPDFLink2, downloadingPDFLink1 } = useSetLinks();
    const { onClickUpdateEvent } = useOnClickUpdateEvent();

    const {
        state,
        answers,
        score,
        totalPercentage,
        overallSectionParagraphs,
        topTips,
        allSectionTips,
        MyPDFDownloadLink,
        chartOptionsWellbeing,
        chartOptionsWorklife,
        chartOptionsWealth,
    } = useAppState();

    const [callUpdateSessionResult] = useLazyQuery(updateSessionResult);

    const updateSessionResults = useCallback(
        (wellbeingscore: string, worklifescore: string, wealthscore: string) => {
            const sessionID = localStorage.getItem('sessionID');

            callUpdateSessionResult({
                variables: {
                    input: {
                        sessionID,
                        wellbeingScore: +wellbeingscore,
                        worklifeScore: +worklifescore,
                        wealthScore: +wealthscore,
                    },
                },
            });
        },
        [callUpdateSessionResult]
    );

    const wellbeingResult = splitStringByDelimiters(overallSectionParagraphs.wellbeingParagraph);
    const worklifeResult = splitStringByDelimiters(overallSectionParagraphs.worklifeParagraph);
    const wealthResult = splitStringByDelimiters(overallSectionParagraphs.wealthParagraph);

    useEffect(() => {
        if (score) {
            updateSessionResults(score?.wellbeingScore, score?.worklifeScore, score?.wealthScore);
        }
    }, [score, updateSessionResults]);

    useEffect(() => {
        const handleScroll = () => {
            if (headerRef.current) {
                const headerHeight = headerRef.current.offsetHeight;
                const scrollTop = window.scrollY || document.documentElement.scrollTop;
                setIsSticky(scrollTop >= headerHeight);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    if (state === null) {
        return (
            <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '24px' }}>
                <Link to="/" style={{ color: '#2E2D2C' }}>
                    Error, please go back home and start assessment
                </Link>
            </div>
        );
    }

    return (
        <ResultsStyle>
            <HeaderStickyBar ref={headerRef} $isSticky={isSticky}>
                <Container>
                    <Row>
                        <Col>
                            <HeaderStickyBarContent>
                                <HeaderStickyBarTitle>Save your results for later</HeaderStickyBarTitle>

                                <HeaderStickyBarDownloadPdf>
                                    <Tooltip>
                                        <TooltipIcon src={IconHelp} alt="icon help" />
                                        <TooltipText>How to download your MOT</TooltipText>

                                        <TooltipHelpBox>
                                            <P>
                                                Simply click "download PDF", wait for an option box to appear, choose
                                                "save as", select a folder to save it to, and rename if needed.
                                            </P>
                                        </TooltipHelpBox>
                                    </Tooltip>

                                    <PdfButtonWrapper
                                        $isDownloading={downloadingPDFLink1}
                                        onClick={() => {
                                            downloadPDFLink1();
                                            onClickUpdateEvent('savedPDFAt');
                                        }}
                                    >
                                        <MyPDFDownloadLink
                                            document={
                                                <ResultsPdf
                                                    overallSectionParagraphs={overallSectionParagraphs}
                                                    answers={answers}
                                                    topTips={topTips}
                                                    allSectionTips={allSectionTips}
                                                    score={score}
                                                    totalPercentage={totalPercentage}
                                                    chartComponentWellbeing={chartComponentWellbeing}
                                                    chartComponentWorklife={chartComponentWorklife}
                                                    chartComponentWealth={chartComponentWealth}
                                                />
                                            }
                                            fileName={`MOT-results-${dayjs().format('DD-MMMM-YYYY')}.pdf`}
                                        >
                                            {({ blob, url, loading, error }: any) =>
                                                url ? 'Download PDF' : 'Loading PDF..'
                                            }
                                        </MyPDFDownloadLink>
                                    </PdfButtonWrapper>
                                </HeaderStickyBarDownloadPdf>
                            </HeaderStickyBarContent>
                        </Col>
                    </Row>
                </Container>
            </HeaderStickyBar>

            <ResultsContainer $isSticky={isSticky}>
                <ResultsFeedbackBox />
                <RuiContainer>
                    <Row>
                        <Col lg={2}></Col>
                        <Col lg={8}>
                            <ResultsTop role="region" aria-label="results mid-life mot">
                                <ResultsTitle>Thanks for completing the Mid-Life MOT</ResultsTitle>
                                <P>
                                    Let's explore your scores in wellbeing, work, and wealth to identify areas of
                                    strength and opportunities for growth. Let's start by looking at your combined
                                    score:
                                </P>
                                <ResultsSubtitle>
                                    Your combined score is <ScoreColor>{totalPercentage}%</ScoreColor>
                                </ResultsSubtitle>
                                <P>{score.overallMessage}</P>

                                <H3>Your Scores Breakdown</H3>
                            </ResultsTop>
                        </Col>
                        <Col lg={2}></Col>
                    </Row>

                    <Row>
                        <Col lg={4}>
                            <ResultsSection $variant="wellbeing">
                                <ResultsSectionImg src={IconWellbeing} alt="icon wellbeing" />
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={chartOptionsWellbeing}
                                    ref={chartComponentWellbeing}
                                />
                                <ResultsSectionTitle>Wellbeing</ResultsSectionTitle>
                                <ResultsSectionScore>{score?.wellbeingScore}%</ResultsSectionScore>

                                {wellbeingResult.map((item, index) =>
                                    index === 0 ? (
                                        <Strong key={index}>{item} </Strong>
                                    ) : (
                                        <span key={index}>{item} </span>
                                    )
                                )}
                            </ResultsSection>
                        </Col>

                        <Col lg={4}>
                            <ResultsSection $variant="worklife">
                                <ResultsSectionImg src={IconWorklife} alt="icon worklife" />
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={chartOptionsWorklife}
                                    ref={chartComponentWorklife}
                                />
                                <ResultsSectionTitle>Work-life</ResultsSectionTitle>
                                <ResultsSectionScore>{score?.worklifeScore}%</ResultsSectionScore>

                                {worklifeResult.map((item, index) =>
                                    index === 0 ? (
                                        <Strong key={index}>{item} </Strong>
                                    ) : (
                                        <span key={index}>{item} </span>
                                    )
                                )}
                            </ResultsSection>
                        </Col>

                        <Col lg={4}>
                            <ResultsSection $variant="wealth">
                                <ResultsSectionImg src={IconWealth} alt="icon wealth" />
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={chartOptionsWealth}
                                    ref={chartComponentWealth}
                                />
                                <ResultsSectionTitle>Wealth</ResultsSectionTitle>
                                <ResultsSectionScore>{score?.wealthScore}%</ResultsSectionScore>

                                {wealthResult.map((item, index) =>
                                    index === 0 ? (
                                        <Strong key={index}>{item} </Strong>
                                    ) : (
                                        <span key={index}>{item} </span>
                                    )
                                )}
                            </ResultsSection>
                        </Col>
                    </Row>

                    <Row>
                        <Col lg={12}>
                            <ResultsImprove>
                                <ResultsImproveTitle>How can I improve my score</ResultsImproveTitle>

                                <ResultsImproveText>
                                    <P>
                                        Follow the top tips below. Make sure you
                                        <span
                                            onClick={() => {
                                                downloadPDFLink2();
                                                onClickUpdateEvent('savedPDFAt');
                                            }}
                                        >
                                            <MyPDFDownloadLink
                                                document={
                                                    <ResultsPdf
                                                        overallSectionParagraphs={overallSectionParagraphs}
                                                        answers={answers}
                                                        topTips={topTips}
                                                        allSectionTips={allSectionTips}
                                                        score={score}
                                                        totalPercentage={totalPercentage}
                                                        chartComponentWellbeing={chartComponentWellbeing}
                                                        chartComponentWorklife={chartComponentWorklife}
                                                        chartComponentWealth={chartComponentWealth}
                                                    />
                                                }
                                                fileName={`MOT-results-${dayjs().format('DD-MMMM-YYYY')}.pdf`}
                                            >
                                                {() => 'download the PDF'}
                                            </MyPDFDownloadLink>
                                        </span>
                                        so you can keep track of your progress. You can also set a reminder down below,
                                        allowing you to check your progress in 6 months time.
                                    </P>
                                </ResultsImproveText>
                            </ResultsImprove>
                        </Col>
                    </Row>

                    <Row>
                        <Col lg={12}>
                            <TopTips topTips={topTips} />
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={12}>
                            <ReminderCard onClickUpdateEvent={onClickUpdateEvent} />
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={12}>
                            <SectionTips
                                wellbeingTips={allSectionTips.wellbeingTips}
                                worklifeTips={allSectionTips.worklifeTips}
                                wealthTips={allSectionTips.wealthTips}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col lg={6}>
                            <ResultsInfoBox
                                svg={IdeaIcon}
                                content={'Looking for more information?'}
                                linkText={'Find additional resources'}
                                linkUrl={'/further-information'}
                            />
                        </Col>
                        <Col lg={6}>
                            <ResultsInfoBox
                                svg={PassionIcon}
                                content={'Need to speak to someone?'}
                                linkText={'Get additional support'}
                                linkUrl={'/help'}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col lg={12}>
                            <ResultsAnswers answers={answers} />
                        </Col>
                    </Row>
                </RuiContainer>
            </ResultsContainer>
        </ResultsStyle>
    );
};
